import { useEffect, useRef } from 'react';

/**
 * Custom hook for handling async operations in useEffect with proper cleanup
 * to prevent memory leaks from state updates on unmounted components.
 * 
 * @param {Function} asyncFn - Async function to execute, receives isMounted function
 * @param {Array} deps - Dependencies array for useEffect
 * @param {Function} onError - Optional error handler function
 */
export const useAsyncEffect = ( asyncFn, deps, onError = null ) => {
  const isMountedRef = useRef( true );

  useEffect( () => {
    isMountedRef.current = true;

    const executeAsync = async () => {
      try {
        // Pass a function that returns the current mounted state
        const isMounted = () => isMountedRef.current;
        await asyncFn( isMounted );
      } catch ( error ) {
        if ( isMountedRef.current ) {
          if ( onError ) {
            onError( error );
          } else {
            console.error( 'Async effect error:', error );
          }
        }
      }
    };

    executeAsync();

    return () => {
      isMountedRef.current = false;
    };
  }, deps );

  // Cleanup on unmount
  useEffect( () => {
    return () => {
      isMountedRef.current = false;
    };
  }, [] );
};

export default useAsyncEffect;
